import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Form, Alert, Card, ProgressBar, Spinner, Badge } from 'react-bootstrap';
import { getQuiz, submitQuiz } from '../../services/api';
import './QuizDetail.css';


const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

const QuizDetail = () => {
    const { id } = useParams();
    const [quiz, setQuiz] = useState(null);
    const [randomizedQuestions, setRandomizedQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [results, setResults] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const questionsPerPage = 5;

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await getQuiz(id);
                const quizData = response.data;
                setQuiz(quizData);

                // Randomize questions and their options
                const shuffledQuestions = shuffleArray(quizData.questions).map(question => ({
                    ...question,
                    options: shuffleArray(question.options)
                }));
                setRandomizedQuestions(shuffledQuestions);
            } catch (error) {
                console.error('Error fetching quiz', error);
                setError('Không thể tải bài thi. Vui lòng thử lại sau.');
                
                // Nếu là lỗi xác thực, API interceptor sẽ xử lý
            } finally {
                setLoading(false);
            }
        };

        fetchQuiz();
    }, [id]);

    const handleChange = (questionId, selectedOption) => {
        setAnswers({
            ...answers,
            [questionId]: selectedOption,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const formattedAnswers = Object.keys(answers).map((questionId) => ({
            questionId,
            selectedOption: answers[questionId],
        }));
        
        try {
            setSubmitting(true);
            setError(null);
            const response = await submitQuiz(id, formattedAnswers);
            setResults(response.data);
        } catch (error) {
            console.error('Error submitting quiz', error);
            setError('Đã xảy ra lỗi khi nộp bài. Vui lòng thử lại.');
            
            // Nếu là lỗi xác thực, API interceptor sẽ xử lý
        } finally {
            setSubmitting(false);
        }
    };

    const handleNextPage = () => {
        setCurrentPage((prevPage) => prevPage + 1);
        window.scrollTo(0, 0);
    };

    const handlePreviousPage = () => {
        setCurrentPage((prevPage) => prevPage - 1);
        window.scrollTo(0, 0);
    };

    const startIndex = (currentPage - 1) * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    const paginatedQuestions = randomizedQuestions.slice(startIndex, endIndex);
    
    const totalPages = Math.ceil(randomizedQuestions.length / questionsPerPage);
    const answeredQuestionsCount = Object.keys(answers).length;
    const totalQuestionsCount = randomizedQuestions.length;

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Đang tải bài thi...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="my-5">
                <Alert variant="danger">
                    <Alert.Heading>Đã xảy ra lỗi</Alert.Heading>
                    <p>{error}</p>
                    <div className="d-flex justify-content-between">
                        <Button variant="outline-danger" onClick={() => navigate('/quizzes')}>
                            Quay lại danh sách
                        </Button>
                        <Button variant="primary" onClick={() => window.location.reload()}>
                            Thử lại
                        </Button>
                    </div>
                </Alert>
            </Container>
        );
    }

    if (!quiz || randomizedQuestions.length === 0) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                <div className="text-center">
                    <Alert variant="warning">
                        <Alert.Heading>Không tìm thấy bài thi</Alert.Heading>
                        <p>Không thể tải thông tin bài thi. Vui lòng thử lại sau.</p>
                        <Button variant="primary" onClick={() => navigate('/quizzes')}>
                            Quay lại danh sách
                        </Button>
                    </Alert>
                </div>
            </Container>
        );
    }

    if (results) {
        return (
            <Container className="my-5">
                <h2 className="text-center mb-4">{quiz.title} - Kết quả</h2>
                <Card className="mb-4">
                    <Card.Body>
                        <Alert variant="info">
                            <Alert.Heading>Điểm của bạn</Alert.Heading>
                            <p className="mb-0">Điểm của bạn {results.score} trên {results.totalQuestions}</p>
                        </Alert>
                        <ProgressBar
                            now={(results.score / results.totalQuestions) * 100}
                            label={`${Math.round((results.score / results.totalQuestions) * 100)}%`}
                            className="mt-3"
                            variant={
                                (results.score / results.totalQuestions) * 100 >= 80 ? "success" :
                                (results.score / results.totalQuestions) * 100 >= 60 ? "info" :
                                (results.score / results.totalQuestions) * 100 >= 40 ? "warning" : "danger"
                            }
                        />
                    </Card.Body>
                </Card>
                {results.results.map((result, index) => {
                    const question = randomizedQuestions.find(q => q._id === result.questionId);
                    return (
                        <Card key={result.questionId} className="mb-3">
                            <Card.Header>
                                <h5 className="mb-0">{`${index + 1}. ${question.questionText}`}</h5>
                            </Card.Header>
                            <Card.Body>
                                {question.options.map((option) => {
                                    const isSelected = result.selectedOption === option.text;
                                    const isCorrect = option.text === result.correctOption;
                                    return (
                                        <Form.Check
                                            key={option._id}
                                            type="radio"
                                            id={`${result.questionId}-${option._id}`}
                                            label={option.text}
                                            checked={isSelected}
                                            disabled
                                            className={`${isCorrect ? 'text-success fw-bold' : (isSelected ? 'text-danger' : '')}`}
                                        />
                                    );
                                })}
                            </Card.Body>
                        </Card>
                    );
                })}
                <div className="text-center">
                    <Button variant="primary" onClick={() => navigate('/quizzes')}>Học Lại</Button>
                </div>
            </Container>
        );
    }

    return (
        <Container className="my-5">
            <h2 className="text-center mb-4">{quiz.title}</h2>
            
            <div className="d-flex justify-content-between mb-3">
                <div>
                    <Badge bg="info">Trang {currentPage}/{totalPages}</Badge>
                </div>
                <div>
                    <Badge bg={answeredQuestionsCount === totalQuestionsCount ? "success" : "warning"}>
                        Đã trả lời: {answeredQuestionsCount}/{totalQuestionsCount}
                    </Badge>
                </div>
            </div>
            
            <Card>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        {paginatedQuestions.map((question, index) => (
                            <div key={question._id} className="mb-4">
                                <h5>{`${startIndex + index + 1}. ${question.questionText}`}</h5>
                                {question.options.map((option) => (
                                    <Form.Check
                                        key={option._id}
                                        type="radio"
                                        id={`${question._id}-${option._id}`}
                                        name={question._id}
                                        value={option.text}
                                        label={option.text}
                                        checked={answers[question._id] === option.text}
                                        onChange={() => handleChange(question._id, option.text)}
                                        className="my-2"
                                    />
                                ))}
                            </div>
                        ))}
                        <Row className="mt-4">
                            <Col>
                                <Button
                                    variant="secondary"
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                >
                                    Trang trước
                                </Button>
                            </Col>
                            <Col className="text-center">
                                <Button 
                                    variant="primary" 
                                    type="submit"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Đang nộp...
                                        </>
                                    ) : (
                                        'Nộp Bài'
                                    )}
                                </Button>
                            </Col>
                            <Col className="d-flex justify-content-end">
                                <Button
                                    variant="secondary"
                                    onClick={handleNextPage}
                                    disabled={endIndex >= randomizedQuestions.length}
                                >
                                    Trang sau
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
            <ProgressBar
                now={(currentPage / Math.ceil(randomizedQuestions.length / questionsPerPage)) * 100}
                label={`${Math.round((currentPage / Math.ceil(randomizedQuestions.length / questionsPerPage)) * 100)}%`}
                className="mt-4"
                variant="info"
            />
            
            <Alert variant="warning" className="mt-4">
                <Alert.Heading>Lưu ý</Alert.Heading>
                <p>
                    Vui lòng đảm bảo bạn đã trả lời tất cả các câu hỏi trước khi nộp bài. 
                    Hiện tại bạn đã trả lời {answeredQuestionsCount} trên tổng số {totalQuestionsCount} câu hỏi.
                </p>
            </Alert>
        </Container>
    );
};

export default QuizDetail;